'use client'

import React from 'react'
import { cn, openInNewTab } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import type { ActionChip } from '@/context/AIContext'

// ---------------------------------------------------------------------------
// ActionChipRow
//
// Renders a horizontal scrolling row of action chip buttons below an AI
// message. Each chip represents a suggested next step (e.g., "View Claim X")
// and opens the relevant page in a new browser tab.
//
// Read-only: all chips are navigation-only, no data mutations.
// ---------------------------------------------------------------------------

// Icon name → component mapping (subset used in chips)
function getChipIcon(iconName?: string): React.ElementType | null {
  switch (iconName) {
    case 'fileText':      return Icons.fileText
    case 'layers':        return Icons.layers
    case 'alertTriangle': return Icons.alertTriangle
    case 'pencil':        return Icons.pencil
    case 'repeat':        return Icons.repeat
    case 'shield':        return Icons.shield
    case 'inbox':         return Icons.inbox
    case 'workbench':     return Icons.workbench
    case 'barChart':      return Icons.barChart
    case 'externalLink':  return Icons.externalLink
    case 'eye':           return Icons.eye
    default:              return null
  }
}

interface ActionChipRowProps {
  chips: ActionChip[]
  className?: string
}

export function ActionChipRow({ chips, className }: ActionChipRowProps) {
  if (!chips || chips.length === 0) return null

  const handleChipClick = (href: string) => {
    // All action chips open in new tab — conversation stays persistent
    openInNewTab(href)
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 mt-2 overflow-x-auto pb-1',
        'scrollbar-none', // hide scrollbar on supporting browsers
        className,
      )}
      role="group"
      aria-label="Suggested actions"
    >
      {chips.map((chip, i) => {
        const ChipIcon = getChipIcon(chip.icon)

        return (
          <button
            key={`${chip.href}-${i}`}
            type="button"
            onClick={() => handleChipClick(chip.href)}
            className={cn(
              // Pill button base
              'inline-flex shrink-0 items-center gap-1.5',
              'rounded-full border px-3 py-1.5',
              'text-xs font-medium whitespace-nowrap',
              // Brand styling
              'border-brand-cornflower/30 bg-brand-cornflower/6',
              'text-brand-navy',
              // Hover / focus
              'hover:bg-brand-cornflower/15 hover:border-brand-cornflower/50',
              'transition-all duration-150 cursor-pointer',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower/40',
            )}
            aria-label={`${chip.label} — opens in new tab`}
            title={`Open in new tab`}
          >
            {ChipIcon && (
              <ChipIcon className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2} />
            )}
            <span>{chip.label}</span>
            <Icons.externalLink
              className="h-2.5 w-2.5 shrink-0 opacity-40"
              strokeWidth={2}
              aria-hidden="true"
            />
          </button>
        )
      })}
    </div>
  )
}
